/**
 * Message Service for handling communication between different window contexts
 * Supports popup windows and direct linked pages
 */

import { getWindowContext, getWindowInfo } from './popup-utils'

/**
 * Message types for session timeout events
 */
export const MESSAGE_TYPES = {
    PAYMENT_SESSION_EXPIRED: 'PAYMENT_SESSION_EXPIRED',
    SESSION_TIMEOUT_WARNING: 'SESSION_TIMEOUT_WARNING',
    SESSION_TIMEOUT_EXPIRED: 'SESSION_TIMEOUT_EXPIRED',
    SESSION_TIMEOUT_CHANGED: 'SESSION_TIMEOUT_CHANGED',
    WINDOW_CLOSING: 'WINDOW_CLOSING',
    PAYMENT_COMPLETE: 'PAYMENT_COMPLETE',
    PAYMENT_CANCELLED: 'PAYMENT_CANCELLED',
    PAYMENT_ERROR: 'PAYMENT_ERROR',
    DIRECT_LINK_SESSION_EXPIRED: 'DIRECT_LINK_SESSION_EXPIRED',
    DIRECT_LINK_TIMEOUT_WARNING: 'DIRECT_LINK_TIMEOUT_WARNING',
}

/**
 * Storage keys for direct link messaging
 */
const STORAGE_KEYS = {
    SESSION_MESSAGE: 'dmg_session_message',
    TIMEOUT_STATE: 'dmg_timeout_state',
    DIRECT_LINK_ORIGIN: 'dmg_direct_link_origin',
}

/**
 * Enhanced message service that handles different window contexts
 */
class MessageService {
    constructor() {
        this.windowContext = getWindowContext()
        this.windowInfo = getWindowInfo()
        this.messageHandlers = new Map()
        this.storageHandlers = new Map()

        // Initialize storage listener for direct link pages
        this.initializeStorageListener()

        // Store the origin for direct link pages
        if (this.windowContext === 'main' && !this.windowInfo.hasOpener) {
            this.storeDirectLinkOrigin()
        }
    }

    /**
     * Initialize storage event listener for cross-tab communication
     */
    initializeStorageListener() {
        window.addEventListener('storage', event => {
            if (event.key === STORAGE_KEYS.SESSION_MESSAGE) {
                const messageData = this.parseStorageMessage(event.newValue)
                if (messageData) {
                    this.handleStorageMessage(messageData)
                }
            }
        })
    }

    /**
     * Store the origin URL for direct link pages
     */
    storeDirectLinkOrigin() {
        try {
            localStorage.setItem(STORAGE_KEYS.DIRECT_LINK_ORIGIN, window.location.origin)
        } catch (error) {
            console.warn('Failed to store direct link origin:', error)
        }
    }

    /**
     * Parse storage message data
     */
    parseStorageMessage(messageString) {
        try {
            return messageString ? JSON.parse(messageString) : null
        } catch (error) {
            console.warn('Failed to parse storage message:', error)
            return null
        }
    }

    /**
     * Handle storage-based messages
     */
    handleStorageMessage(messageData) {
        const handlers = this.storageHandlers.get(messageData.type)
        if (handlers) {
            handlers.forEach(handler => {
                try {
                    handler(messageData)
                } catch (error) {
                    console.error('Error in storage message handler:', error)
                }
            })
        }
    }

    /**
     * Send a message based on the current window context
     * @param {Object} message - The message to send
     * @param {Object} options - Additional options
     * @param {string} options.targetOrigin - Target origin for postMessage (default: '*')
     * @param {boolean} options.useStorage - Force use of localStorage for direct links
     * @param {number} options.storageExpiry - Storage expiry time in milliseconds
     * @returns {boolean} True if message was sent successfully
     */
    sendMessage(message, options = {}) {
        const {
            targetOrigin = '*',
            useStorage = false,
            storageExpiry = 5 * 60 * 1000, // 5 minutes default
        } = options

        const enhancedMessage = {
            ...message,
            source: 'dmg-payment-sdk',
            timestamp: new Date().toISOString(),
            windowContext: this.windowContext,
            sessionId: this.getSessionId(),
        }

        let messageSent = false

        try {
            // For popup windows
            if (this.windowContext === 'popup' && window.opener && !window.opener.closed) {
                window.opener.postMessage(enhancedMessage, targetOrigin)
                messageSent = true
                // eslint-disable-next-line no-console
                console.log('[MessageService] Sent popup message:', enhancedMessage)
            }
            // For direct link pages or when storage is forced
            else if (this.windowContext === 'main' || useStorage) {
                this.sendStorageMessage(enhancedMessage, storageExpiry)
                messageSent = true
                // eslint-disable-next-line no-console
                console.log('[MessageService] Sent storage message:', enhancedMessage)
            }
            // Also try broadcasting to all windows if it's a critical message
            if (this.isCriticalMessage(message.type)) {
                this.broadcastMessage(enhancedMessage, targetOrigin)
            }
        } catch (error) {
            console.error('[MessageService] Error sending message:', error)
        }

        return messageSent
    }

    /**
     * Send message via localStorage for direct link pages
     */
    sendStorageMessage(message, expiry) {
        try {
            const storageMessage = {
                ...message,
                expiry: Date.now() + expiry,
                id: this.generateMessageId(),
            }

            localStorage.setItem(STORAGE_KEYS.SESSION_MESSAGE, JSON.stringify(storageMessage))
            // Clean up after a short delay to prevent accumulation
            setTimeout(() => {
                try {
                    const currentMessage = localStorage.getItem(STORAGE_KEYS.SESSION_MESSAGE)
                    if (currentMessage) {
                        const parsed = JSON.parse(currentMessage)
                        if (parsed.id === storageMessage.id) {
                            localStorage.removeItem(STORAGE_KEYS.SESSION_MESSAGE)
                        }
                    }
                } catch (error) {
                    console.warn('Failed to clean up storage message:', error)
                }
            }, 1000)
        } catch (error) {
            console.error('[MessageService] Failed to send storage message:', error)
        }
    }

    /**
     * Broadcast message to all possible targets
     */
    broadcastMessage(message, targetOrigin = '*') {
        try {
            // Try popup
            if (window.opener && !window.opener.closed) {
                window.opener.postMessage(message, targetOrigin)
            }

            // Try storage
            this.sendStorageMessage(message, 5 * 60 * 1000)
        } catch (error) {
            console.warn('[MessageService] Error in broadcast:', error)
        }
    }

    /**
     * Check if a message type is critical and should be broadcasted
     */
    isCriticalMessage(messageType) {
        const criticalTypes = [
            MESSAGE_TYPES.PAYMENT_SESSION_EXPIRED,
            MESSAGE_TYPES.SESSION_TIMEOUT_EXPIRED,
            MESSAGE_TYPES.SESSION_TIMEOUT_CHANGED,
            MESSAGE_TYPES.DIRECT_LINK_SESSION_EXPIRED,
            MESSAGE_TYPES.WINDOW_CLOSING,
        ]
        return criticalTypes.includes(messageType)
    }

    /**
     * Generate a unique message ID
     */
    generateMessageId() {
        const randomBytes = new Uint32Array(2)
        crypto.getRandomValues(randomBytes)
        return `msg_${Date.now()}_${randomBytes[0].toString(36)}${randomBytes[1].toString(36)}`
    }

    /**
     * Get session ID from URL or generate one
     */
    getSessionId() {
        try {
            const urlParams = new URLSearchParams(window.location.search)
            return urlParams.get('sessionId') || window.location.pathname.split('/').pop() || 'unknown'
        } catch (error) {
            return 'unknown'
        }
    }

    /**
     * Register a message handler for postMessage events
     */
    onMessage(messageType, handler) {
        if (!this.messageHandlers.has(messageType)) {
            this.messageHandlers.set(messageType, new Set())
        }
        this.messageHandlers.get(messageType).add(handler)

        // Return unsubscribe function
        return () => {
            const handlers = this.messageHandlers.get(messageType)
            if (handlers) {
                handlers.delete(handler)
                if (handlers.size === 0) {
                    this.messageHandlers.delete(messageType)
                }
            }
        }
    }

    /**
     * Register a storage message handler for localStorage events
     */
    onStorageMessage(messageType, handler) {
        if (!this.storageHandlers.has(messageType)) {
            this.storageHandlers.set(messageType, new Set())
        }
        this.storageHandlers.get(messageType).add(handler)

        // Return unsubscribe function
        return () => {
            const handlers = this.storageHandlers.get(messageType)
            if (handlers) {
                handlers.delete(handler)
                if (handlers.size === 0) {
                    this.storageHandlers.delete(messageType)
                }
            }
        }
    }

    /**
     * Send session timeout warning message
     */
    sendTimeoutWarning(remainingTime, options = {}) {
        return this.sendMessage({
            type:
                this.windowContext === 'main' ? MESSAGE_TYPES.DIRECT_LINK_TIMEOUT_WARNING : MESSAGE_TYPES.SESSION_TIMEOUT_WARNING,
            remainingTime,
            formattedTime: this.formatTime(remainingTime),
            ...options,
        })
    }

    /**
     * Send session timeout changed message
     */
    sendTimeoutChanged(newTimeout, reason, options = {}) {
        return this.sendMessage({
            type: MESSAGE_TYPES.SESSION_TIMEOUT_CHANGED,
            newTimeout,
            formattedTime: this.formatTime(newTimeout),
            reason,
            ...options,
        })
    }

    /**
     * Send session expired message
     */
    sendSessionExpired(reason = 'Session timeout', options = {}) {
        return this.sendMessage({
            type:
                this.windowContext === 'main' ? MESSAGE_TYPES.DIRECT_LINK_SESSION_EXPIRED : MESSAGE_TYPES.PAYMENT_SESSION_EXPIRED,
            reason,
            ...options,
        })
    }

    /**
     * Send window closing message
     */
    sendWindowClosing(reason = 'Window closing', options = {}) {
        return this.sendMessage({
            type: MESSAGE_TYPES.WINDOW_CLOSING,
            reason,
            ...options,
        })
    }

    /**
     * Format time in a human-readable format
     */
    formatTime(milliseconds) {
        const seconds = Math.ceil(milliseconds / 1000)
        const minutes = Math.floor(seconds / 60)
        const remainingSeconds = seconds % 60

        if (minutes > 0) {
            return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
        }
        return `${seconds}s`
    }

    /**
     * Clean up resources
     */
    destroy() {
        this.messageHandlers.clear()
        this.storageHandlers.clear()

        // Clean up storage
        try {
            localStorage.removeItem(STORAGE_KEYS.SESSION_MESSAGE)
            localStorage.removeItem(STORAGE_KEYS.TIMEOUT_STATE)
        } catch (error) {
            console.warn('Failed to clean up storage:', error)
        }
    }
}

// Create singleton instance
const messageService = new MessageService()

export default messageService
export { MessageService }
